import apiClient from './apiClient';

export interface ServiceRevenueItem {
    id: string;
    name: string;
    revenue: number;
    count: number;
}

export interface FinancialStatsResponse {
    totalRevenue: number;
    totalCommissions: number;
    netProfit: number;
    profitMargin: number;
    averageTicket: number;
    growthPercentage: number;
    totalAppointments: number;
    completedAppointments: number;
    topServices: ServiceRevenueItem[];
}

/**
 * Format date as YYYY-MM-DD for API calls
 */
const formatDate = (date: Date): string => {
    return date.toISOString().split('T')[0];
};

export const financesApi = {
    /**
     * Get financial statistics for a salon within a date range
     */
    getFinancialStats: async (
        salonId: string,
        startDate: Date,
        endDate: Date
    ): Promise<FinancialStatsResponse> => {
        const response = await apiClient.get('/stats/financial', {
            params: {
                salonId,
                startDate: formatDate(startDate),
                endDate: formatDate(endDate),
            },
        });
        return response.data;
    },

    /**
     * Get financial stats for today
     */
    getTodayStats: async (salonId: string): Promise<FinancialStatsResponse> => {
        const today = new Date();
        return financesApi.getFinancialStats(salonId, today, today);
    },

    /**
     * Get financial stats for current week (Monday to Sunday)
     */
    getWeekStats: async (salonId: string): Promise<FinancialStatsResponse> => {
        const now = new Date();
        const dayOfWeek = now.getDay();
        const monday = new Date(now);
        monday.setDate(now.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        return financesApi.getFinancialStats(salonId, monday, sunday);
    },

    /**
     * Get financial stats for current month
     */
    getMonthStats: async (salonId: string): Promise<FinancialStatsResponse> => {
        const now = new Date();
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        return financesApi.getFinancialStats(salonId, firstDay, lastDay);
    },

    /**
     * Get financial stats for current year
     */
    getYearStats: async (salonId: string): Promise<FinancialStatsResponse> => {
        const now = new Date();
        const firstDay = new Date(now.getFullYear(), 0, 1);
        const lastDay = new Date(now.getFullYear(), 11, 31);
        return financesApi.getFinancialStats(salonId, firstDay, lastDay);
    },
};
